import React, {useEffect, useRef, useState} from 'react';
import {connect, DispatchProp} from 'react-redux'
import {RootState} from "../../../reducers";
import {useParams} from "react-router-dom";
import Analytics from "../../services/Common/Analytics/Analytics";
import Pixel from "../../services/Common/Analytics/Pixel";
import LoadingContainer from "../../components/Common/LoadingContainer";
import NavbarCart from "../../components/Navbar/NavbarCart";
import {useTranslation} from "react-i18next";
import {getItems, getModifierGroups} from "../../services/Menu/reducer";
import {getStoreConfig} from "../../services/Store/reducer";
import SummarySummaryComponent from "./components/SummarySummaryComponent";
import SummaryContactComponent from "./components/SummaryContactComponent";
import SummaryItemsComponent from "./components/SummaryItemsComponent";
import ErrorView from "../../components/Common/ErrorView";
import {api} from "../../services/Api/api";
import SummaryOrderDetailsComponent from "./components/SummaryOrderDetailsComponent";
import SummaryOrderDataComponent from "./components/SummaryOrderDataComponent";
import SummaryStatusComponent from "./components/SummaryStatusComponent";
import FetchData from "../../components/Fetch/FetchData";
import NetworkToast from "./components/NetworkAlert";
import {OrderApi} from "../../services/Api/types";
import OrderPreparationStatusComponent from "./components/OrderPreparationStatus/OrderPreparationStatusComponent";

type Props = ReturnType<typeof mapStateToProps> & DispatchProp;

const SummaryPage = (props: Props) => {
    const [pending, setPending] = useState(true);
    const [order, setOrder] = useState<OrderApi | undefined>();
    const [error, setError] = useState<any>(undefined);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const {t} = useTranslation();
    const {id, token} = useParams();
    const fetchedOnce = useRef(false);
    const [showNetworkAlert, setShowNetworkAlert] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        const handleOnline = () => {
            setShowNetworkAlert(false);
            refreshOrder(id, token, true);
        };
        const handleOffline = () => {
            if (fetchedOnce.current) setShowNetworkAlert(true);
            abortControllerRef.current?.abort();
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        setPending(true);
        refreshOrder(id, token);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            abortControllerRef.current?.abort();

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);

    const getMessageForError = (error: any, alwaysDisplay?: boolean) => {
        if (typeof error === "string") return error;

        const errorMessage = error?.message;

        if ((!errorMessage || errorMessage === "Unknown") && alwaysDisplay) {
            return t("constraints.internal.loading_data_error");
        }

        if (alwaysDisplay) return errorMessage ?? "";
    }

    const isNetworkOrServerError = (error: any) => {
        if (error?.code === 'ECONNABORTED') return true

        if (!error?.response) return true;

        const status = error.response.status;

        return status >= 500
    };

    const refreshOrder = async (id: any, token: any, withoutPending?: any, withRefresh?: any) => {
        if (!shouldRefreshing()) {
            setPending(false);
            return;
        }

        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const params: Record<string, any> = {};
            if (withRefresh) params.refresh = "1";
            params.include = "all,tracking";
            const data = await api.getOrder(id, token, params, controller.signal);

            if (!withoutPending) {
                Analytics.purchase(data);
                Pixel.purchase(data);
            }

            setOrder(data);
            setPending(false);
            fetchedOnce.current = true;
            setShowNetworkAlert(false);

            if (data.status === 'CONFIRMED' && data.payment_status === 'PROGRESS') {
                withRefresh = true;
            }

            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                refreshOrder(id, token, true, withRefresh);
            }, 5000);
        } catch (error: any) {
            if (error?.name === 'AbortError' || error?.name === 'CanceledError') return;

            const isNetworkError = isNetworkOrServerError(error);

            if (isNetworkError) {
                if (fetchedOnce.current) {
                    setShowNetworkAlert(true);
                }

                if (timeoutRef.current) clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => {
                    refreshOrder(id, token, true, withRefresh);
                }, 5000);

                return;
            }

            setError(error);
            setPending(false);
        } finally {

        }
    }

    const shouldRefreshing = () => {
        if (!order) {
            return true;
        }
        if (order.status == 'OPEN' || order.status == 'CONFIRMED' || order.status == 'WAITING_FOR_ACCEPTED') {
            return true;
        }
        return false;
    }

    if (pending || !order) return (
        <div>

            <LoadingContainer/>
        </div>
    );

    if (error) {
        return (
            <div>
                <ErrorView>
                    {getMessageForError(error)}
                </ErrorView>
            </div>
        );
    }

    return (
        <FetchData menus={true}>
            <NetworkToast show={showNetworkAlert}/>
            <div className="summary-container">
                <NavbarCart/>
                <div className="cart">
                    <SummaryStatusComponent onUpdate={(order) => setOrder(order)} order={order} config={props.config}/>
                    <OrderPreparationStatusComponent order={order} onUpdate={(order: OrderApi) => setOrder(order)}/>
                    <div className="cart cart-container">
                        <SummaryItemsComponent orderItems={order.items} items={props.items}
                                               promotions={order.promotions}
                                               modifierGroups={props.modifierGroups}/>
                        <SummaryOrderDetailsComponent order={order} config={props.config}/>
                        <SummaryContactComponent order={order}/>
                        <SummaryOrderDataComponent order={order}/>
                        <SummarySummaryComponent order={order}/>
                    </div>
                </div>
            </div>
        </FetchData>
    )
}

const mapStateToProps = (state: RootState) => ({
    items: getItems(state.menu),
    modifierGroups: getModifierGroups(state.menu),
    config: getStoreConfig(state.config)
})

const connector = connect(mapStateToProps);

export default connector(SummaryPage);